// AvatarDropdown — pen frame iWIXu (浮层-代理头像下拉)
//
// 顶部 avatar 点击展开 menu:修改密码(跳 /settings)/ 帮助(跳 ticket)/ 分隔 / 退出登录
// (2026-07-01:/profile 页已删,个人资料项移除;修改密码跳设置页)
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, HelpCircle, KeyRound } from 'lucide-react'
import { useCurrentUser } from '@dogdisk/shared'

export function AvatarDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const { user, signOut } = useCurrentUser()

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const initial = (user?.username ?? '?').slice(0, 1).toUpperCase()

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
        style={{ backgroundColor: '#F59E0B' }}
        title={user?.username ?? ''}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initial}
      </button>

      {open && (
        <div
          data-testid="iWIXu"
          data-frame-id="iWIXu"
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-[240px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-[13px] font-semibold text-gray-900">{user?.username ?? '代理'}</p>
            <p className="truncate text-[11px] text-gray-400">{user?.email ?? '—'}</p>
          </div>
          <DropdownItem
            icon={<KeyRound size={16} />}
            label="修改密码"
            onClick={() => {
              setOpen(false)
              navigate('/settings')
            }}
          />
          <DropdownItem
            icon={<HelpCircle size={16} />}
            label="帮助 & 客服"
            onClick={() => {
              setOpen(false)
              // 跳 user SPA ticket 入口(待 §A5 SPA 联调,这里只在 alert)
              const ticketURL = '/#/tickets/new?from=agent'
              window.location.href = ticketURL
            }}
          />
          <div className="my-1 border-t border-gray-100" />
          <DropdownItem
            icon={<LogOut size={16} className="text-red-500" />}
            label="退出登录"
            danger
            onClick={() => {
              setOpen(false)
              // 先 await signOut 再命令式跳 /login(BUG-C11-1)
              void signOut().then(() => navigate('/login', { replace: true }))
            }}
          />
        </div>
      )}
    </div>
  )
}

function DropdownItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-3 px-4 py-2.5 text-left text-[13px] hover:bg-gray-50',
        danger ? 'text-red-600' : 'text-gray-700',
      ].join(' ')}
    >
      <span className={danger ? 'text-red-500' : 'text-gray-500'}>{icon}</span>
      <span>{label}</span>
    </button>
  )
}
